import type { GetStaticPaths, GetStaticProps } from 'next';
import type { PageProps } from 'lib/queries/pages';
import type { Page } from 'types/page';
import React from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Wrap } from '@leafwell/components';
import ArticleJson from 'components/structured-data/article';
import WebPageJson from 'components/structured-data/webpage';
import ProductJson from 'components/structured-data/product';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';

import { getAllStatePages, getPage } from 'lib/queries/pages';

const ArchiveStrains = dynamic(() => import('components/archive-strains'));
const Dashboard = dynamic(() => import('components/dashboard'));
const HeroAbout = dynamic(() => import('components/hero-about'));
const HeroHighlight = dynamic(() => import('components/hero-highlight'));
const HeroText = dynamic(() => import('components/hero-text'));
const HeroMyBusiness = dynamic(() => import('components/hero-my-business'));
const HeroRichTitle = dynamic(() => import('components/hero-rich-title'));
const HeroCard = dynamic(() => import('components/hero-card'));
const HeroCannabisGuidance = dynamic(
  () => import('components/hero-cannabis-guidance'),
);
const HeroPartner = dynamic(() => import('components/hero-partner'));
const HeroCareers = dynamic(() => import('components/hero-careers'));
const HeroVideo = dynamic(() => import('components/hero-video'));
const HeroDownload = dynamic(() => import('components/hero-download'));
const HeroAffiliate = dynamic(() => import('components/hero-affiliate'));
const JobsList = dynamic(() => import('components/jobs-list'));
const Loader = dynamic(() => import('components/loader'));
const LocationsSearch = dynamic(() => import('components/locations-search'), {
  ssr: false,
  loading: () => {
    //to prevent layout shift
    return <div className="min-h-screen" />;
  },
});
const StateLawsList = dynamic(() => import('components/state-laws-list'));
const WysiwygContent = dynamic(() => import('components/wysiwyg-content'));
const LinktreeContent = dynamic(() => import('components/linktree-content'));

const PageTopExtraContent: React.FC<
  Pick<PageProps['page']['acf'], 'pageType'> & Pick<PageProps, 'locations'>
> = ({ locations, pageType }) => {
  switch (pageType) {
    case 'partners_map':
      return <LocationsSearch {...{ locations, variant: 'secondary' }} />;
    default:
      return null;
  }
};

const PageBottomExtraContent: React.FC<
  Pick<PageProps['page']['acf'], 'pageType'> &
    Pick<PageProps, 'lawStatuses' | 'strains' | 'teams'>
> = ({ pageType, lawStatuses, strains, teams }) => {
  switch (pageType) {
    case 'laws':
      return <StateLawsList lawStatuses={lawStatuses} />;
    case 'careers':
      return <JobsList teams={teams} />;
    case 'strains_archive':
      return <ArchiveStrains initialData={strains} />;
    default:
      return null;
  }
};

const PageHero: React.FC<
  Pick<
    PageProps['page']['acf'],
    | 'hero'
    | 'states'
    | 'formUrl'
    | 'pageType'
    | 'affiliateLogo'
    | 'affiliateWebsite'
    | 'pauseAppointments'
    | 'redirectMedicalCard'
    | 'redirectCannabisCounseling'
    | 'hasReferral'
  > &
    Pick<PageProps, 'variant' | 'vpdEvents'>
> = ({
  hero,
  states,
  formUrl,
  pageType,
  affiliateLogo,
  affiliateWebsite,
  pauseAppointments,
  redirectMedicalCard,
  redirectCannabisCounseling,
  variant,
  vpdEvents,
  hasReferral,
}) => {
  if (!hero) {
    return null;
  }

  switch (pageType) {
    case 'about':
      return <HeroAbout {...hero} />;
    case 'blocks':
      return <HeroRichTitle {...hero} />;
    case 'card':
      return (
        <HeroCard
          hero={hero}
          pageType={pageType}
          states={states}
          formUrl={formUrl}
          affiliateLogo={affiliateLogo}
          affiliateWebsite={affiliateWebsite}
          pauseAppointments={pauseAppointments}
          redirectMedicalCard={redirectMedicalCard}
          redirectCannabisCounseling={redirectCannabisCounseling}
          variant={variant}
          showBadges={false}
          showStateSelect={true}
        />
      );
    case 'cannabis_counseling':
      return (
        <HeroCannabisGuidance
          hero={hero}
          pageType={pageType}
          redirectCannabisCounseling={redirectCannabisCounseling}
        />
      );
    case 'partners':
      return <HeroPartner hero={hero} pageType={pageType} />;
    case 'laws':
      return <HeroText {...hero} layout="column" />;
    case 'careers':
      return <HeroCareers hero={hero} />;
    case 'dashboard':
      return <HeroHighlight hero={hero} />;
    case 'strains_archive':
      return <HeroText {...hero} />;
    case 'google_my_business':
      return <HeroMyBusiness hero={hero} pageType={pageType} />;
    case 'app_landing':
      return <HeroVideo hero={hero} />;
    case 'quarterly_report':
      return <HeroDownload hero={hero} />;
    case 'affiliate_landing':
      return (
        <HeroAffiliate
          hero={hero}
          formUrl={formUrl}
          pageType={pageType}
          states={states}
          affiliateLogo={affiliateLogo}
          affiliateWebsite={affiliateWebsite}
          redirectMedicalCard={redirectMedicalCard}
          hasReferral={hasReferral}
          redirectCannabisCounseling={redirectCannabisCounseling}
          vpdEvents={vpdEvents}
        />
      );
    default:
      return null;
  }
};

// This is only to test the WebPageJson and should be removed after we get some results.
const statesWithWebPageAndProductSchema = [
  'pennsylvania',
  'california',
  'ohio',
  'louisiana',
  'massachusetts',
  'new-york',
  'new-jersey',
  'oklahoma',
  'illinois',
  'virginia',
  'texas',
];

const PageContent: React.FC<PageProps> = ({
  dashboard,
  page: {
    id,
    title,
    content,
    seo,
    date,
    modified,
    slug,
    acf: {
      hero,
      name,
      affiliateLogo,
      affiliateWebsite,
      formUrl,
      pauseAppointments,
      redirectMedicalCard,
      redirectCannabisCounseling,
      description,
      imageObjectCaption,
      blocks,
      pageType,
      linktree,
      stateProductImage,
      product,
      totalMedicalCardPrice,
      states,
      hasReferral,
    },
  },
  lawStatuses,
  locations,
  partnersReviews,
  strains,
  teams,
  variant,
  vpdEvents,
  settings: { seoSettings },
}) => {
  const hasWebPageAndProductJson =
    pageType === 'card' && statesWithWebPageAndProductSchema.includes(slug);

  return (
    <PageWrap seo={seo} pageType={pageType} title={title}>
      {hasWebPageAndProductJson ? (
        <>
          <WebPageJson
            seo={seo}
            date={date}
            title={title}
            modified={modified}
            stateName={slug
              .replace(/-/g, ' ')
              .replace(/\b\w/g, function (letter) {
                return letter.toUpperCase();
              })}
            siteUrl={seoSettings.siteUrl}
            name={name}
            description={description}
            imageObjectCaption={imageObjectCaption}
            siteDescription={seoSettings.homepage.homepageDescription}
          />
          <ProductJson
            product={product}
            totalMedicalCardPrice={totalMedicalCardPrice}
          />
        </>
      ) : (
        <ArticleJson id={id} date={date} modified={modified} seo={seo} />
      )}
      <PageTopExtraContent locations={locations} pageType={pageType} />
      <PageHero
        hero={hero}
        states={states}
        formUrl={formUrl}
        pageType={pageType}
        affiliateLogo={affiliateLogo}
        affiliateWebsite={affiliateWebsite}
        pauseAppointments={pauseAppointments}
        redirectMedicalCard={redirectMedicalCard}
        redirectCannabisCounseling={redirectCannabisCounseling}
        variant={variant}
        vpdEvents={vpdEvents}
        hasReferral={hasReferral}
      />
      {pageType === 'dashboard' && <Dashboard initialData={dashboard} />}
      {content ? (
        <Wrap size="small" className="justify-items-center">
          <WysiwygContent className="w-full max-w-2xl" content={content} />
        </Wrap>
      ) : null}
      {blocks && blocks.length > 0 && pageType !== 'meta_analysis' && (
        <Blocks
          blocks={blocks}
          pageType={pageType}
          states={states}
          modified={modified}
          partnersReviews={partnersReviews}
          stateProductImage={stateProductImage}
          hasReferral={hasReferral}
        />
      )}
      {pageType === 'linktree' && <LinktreeContent linktree={linktree} />}
      <PageBottomExtraContent
        pageType={pageType}
        lawStatuses={lawStatuses}
        strains={strains}
        teams={teams}
      />
    </PageWrap>
  );
};

const Page: React.FC<PageProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <PageContent {...props} />;
};

export const getStaticProps: GetStaticProps = async context => {
  const variant = undefined;

  // if (Array.isArray(params.slug)) {
  //   const lastSlugItem = params.slug[params.slug.length - 1];

  //   // Check for variant for a/b testing
  //   if (lastSlugItem.search(/\b(?:test|control)\b/g) > -1) {
  //     variant = params.slug.pop();
  //     params.slug = getExperimentSlugByVariant(params.slug, variant);
  //   }
  // }

  const contextData = {
    ...context,
    params: {
      ...context.params,
      slug:
        context.params?.slug?.length === 1 &&
        context.params?.slug[0] === 'cannabis-strains-directory'
          ? ['cannabis-strains', ...context.params?.slug]
          : context.params?.slug,
    },
  };

  return await getPage({
    variant,
    ...contextData,
  });
};

export const getStaticPaths: GetStaticPaths = async context => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION === 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  // experiment variant paths that we want to statically generate
  // e.g. /medical-card/california/test or /medical-card/california/control
  //const variantPaths = await getExperimentVariantPaths(pages);
  const allStatePages = await getAllStatePages();

  const paths = allStatePages.map(({ slug, locale }) => ({
    params: {
      slug,
    },
    locale,
  }));

  return { paths, fallback: 'blocking' };
};

export default Page;
